import { Sparkles } from "lucide-react-native";
import { FC, useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

import {
  Box,
  HStack,
  Progress,
  ProgressFilledTrack,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

type SummaryLineProps = { delay: number; text: string };

const SummaryLine: FC<SummaryLineProps> = ({ delay, text }) => {
  const opacity = useSharedValue(0);

  useEffect(() => {
    opacity.value = withDelay(delay, withTiming(1, { duration: 500 }));
  }, [delay, opacity]);

  const style = useAnimatedStyle(() => ({ opacity: opacity.value }));

  return (
    <Animated.View style={style}>
      <Text className="text-sm text-typography-500 leading-relaxed">
        {text}
      </Text>
    </Animated.View>
  );
};

interface SummariesLoaderProps {
  progress: number;
  total: number;
}

const SummariesLoader: FC<SummariesLoaderProps> = ({ progress, total }) => {
  const { i18n } = useLocalization();
  const pct = Math.round(progress * 100);

  if (!total) {
    return (
      <Box className="rounded-2xl p-5 gap-4 mt-8">
        <HStack className="items-center gap-2">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="h-4 w-32 rounded" />
        </HStack>
        <Skeleton className="h-3.5 w-full rounded" />
        <Skeleton className="h-3.5 w-full rounded" />
        <Skeleton className="h-3.5 w-5/6 rounded" />
        <Skeleton className="h-3.5 w-3/4 rounded" />
        <VStack className="gap-1.5 mt-2">
          <Skeleton className="h-1.5 w-full rounded-full" />
          <Skeleton className="h-3 w-10 rounded self-end" />
        </VStack>
      </Box>
    );
  }

  const lines = [
    i18n.t("studies.summaryLoadingAnalyzing"),
    i18n.t("studies.summaryLoadingIdentifying"),
    i18n.t("studies.summaryLoadingCompiling"),
    i18n.t("studies.summaryLoadingDrafting"),
  ];

  return (
    <Box className="rounded-2xl p-5 gap-4 mt-8">
      <HStack className="items-center gap-2">
        <Sparkles
          color="rgb(var(--color-primary-500))"
          size={16}
          strokeWidth={2}
        />
        <Text className="text-sm font-semibold text-typography-900">
          {i18n.t("studies.keyTakeaways")}
        </Text>
      </HStack>
      {lines.map((line, i) => (
        <SummaryLine key={i} delay={i * 700} text={line} />
      ))}
      <VStack className="gap-1.5 mt-2">
        <Progress className="w-full bg-background-100" size="xs" value={pct}>
          <ProgressFilledTrack className="bg-tertiary-500 [transition:width_0.3s_ease-out]" />
        </Progress>
        <Text className="text-xs font-semibold text-tertiary-500 text-right">
          {i18n.numberToPercentage(pct, { precision: 0 })}
        </Text>
      </VStack>
    </Box>
  );
};

export default SummariesLoader;
